import React from 'react';
import { Table } from 'reactstrap';

export const EqIntensityModal = {
  title: 'MMI Intensity',
  body: (
    <div>
      <div>
        Valid values:
        {' '}
        <b>1 to 12</b>
      </div>
      <br />
      <p>
        The Modified Mercalli Intensity (Int) is given in Roman Numerals (converted to numbers in the digital database). An interpretation (Wood and Neumann, 1931) of the values is listed below in Table 1.
      </p>
      <p>
        However, Dewey, et al. (1995) state that “it has become clear that many phenomena that Wood and Neumann (1931) originally used as criteria to define the highest Modified Mercalli intensities (X and above) are related less to the level of ground shaking than to the presence of ground conditions susceptible to spectacular failure or to the ease with which seismic faulting of different style and depth can propagate to the ground surface. Criteria based on such phenomena are downweighted now in assigning of USGS intensities (Stover and Coffman, 1993)”. As a result, for recent events Intensity X (10) is the highest value on the MMI.
      </p>
      <p>
        Recently, the USGS has begun to provide Community Decimal Intensity (CDI) values, instead of observed MMI. CDIs are not individual observations. Rather they are, by definition, a measure of earthquake effects over an area. Historically, USGS use the ZIP code as the default area. For responses outside the U.S., they instead use the nearest large city and plot that location as a circle with size dependent on the city population. CDI values should, on average, be similar to the MMI values for the same communities (Dengler and Dewey, 1998).
      </p>
      <h5>Table 1. Modified Mercalli Intensity Scale of 1931</h5>
      <Table size="sm">
        <tbody>
          <tr>
            <th scope="row">I.</th>
            <td>Not felt except by a very few under especially favorable circumstances.</td>
          </tr>
          <tr>
            <th scope="row">II.</th>
            <td>Felt only by a few persons at rest, especially on upper floors of buildings. Delicately suspended objects may swing.</td>
          </tr>
          <tr>
            <th scope="row">III.</th>
            <td>Felt quite noticeably indoors, especially on upper floors of buildings, but many people do not recognize it as an earthquake. Standing motor cars may rock slightly. Vibration like passing truck. Duration estimated.</td>
          </tr>
          <tr>
            <th scope="row">IV.</th>
            <td>During the day felt indoors by many, outdoors by few. At night some awakened. Dishes, windows, and doors disturbed; walls make creaking sound. Sensation like heavy truck striking building. Standing motorcars rock noticeably.</td>
          </tr>
          <tr>
            <th scope="row">V.</th>
            <td>Felt by nearly everyone; many awakened. Some dishes, windows, etc., broken; a few instances of cracked plaster; unstable objects overturned. Disturbance of trees, poles, and other tall objects sometimes noticed. Pendulum clocks may stop.</td>
          </tr>
          <tr>
            <th scope="row">VI.</th>
            <td>Felt by all; many frightened and run outdoors. Some heavy furniture moved; a few instances of fallen plaster or damaged chimneys. Damage slight.</td>
          </tr>
          <tr>
            <th scope="row">VII.</th>
            <td>Everybody runs outdoors. Damage negligible in buildings of good design and construction slight to moderate in well built ordinary structures; considerable in poorly built or badly designed structures. Some chimneys broken. Noticed by persons driving motor cars.</td>
          </tr>
          <tr>
            <th scope="row">VIII.</th>
            <td>Damage slight in specially designed structures; considerable in ordinary substantial buildings, with partial collapse; great in poorly built structures. Panel walls thrown out of frame structures. Fall of chimneys, factory stacks, columns, monuments, walls. Heavy furniture overturned. Sand and mud ejected in small amounts. Changes in well water. Persons driving motor cars disturbed.</td>
          </tr>
          <tr>
            <th scope="row">IX.</th>
            <td>Damage considerable in specially designed structures; well-designed frame structures thrown out of plumb; great in substantial buildings, with partial collapse. Buildings shifted off foundations. Ground cracked conspicuously. Underground pipes broken.</td>
          </tr>
          <tr>
            <th scope="row">X.</th>
            <td>Some well-built wooden structures destroyed; most masonry and frame structures destroyed with foundations; ground badly cracked. Rails bent. Landslides considerable from river banks and steep slopes. Shifted sand and mud. Water splashed over banks.</td>
          </tr>
          <tr>
            <th scope="row">XI.</th>
            <td>Few, if any (masonry), structures remain standing. Bridges destroyed. Broad fissures in ground. Underground pipelines completely out of service. Earth slumps and land slips in soft ground. Rails bent greatly.</td>
          </tr>
          <tr>
            <th scope="row">XII.</th>
            <td>Damage total. Waves seen on ground surfaces. Lines of sight and level distorted. Objects thrown upward into the air.</td>
          </tr>
        </tbody>
      </Table>
      <p>References:</p>
      <p>Dewey, James, B. Glen Reagor, L. Dengler, K. Moley (1995). Intensity Distribution and Isoseismal Maps for the Northridge, California, Earthquake of January 17,1994, USGS Open-File Report 95-92</p>
      <p>Harry O. Wood and Frank Neumann, in Bulletin of the Seismological Society of America, Vol. 21, No. 4, December 1931. es Geophysicae, 4B, (6), Paris, France, 1986, pp. 679-702.</p>
      <p>Stover, C. W., and Coffman, J. L. (1993). Seismicity of the United States, 1568-1989 (Revised): U. S. Geological Survey Professional Paper 1527, 418 p.</p>
      <br />
      <br />
      <h5>Comparison of Modified Mercalli (MM) and Other Intensity Scales</h5>
      <Table size="sm">
        <thead>
          <tr>
            <th>Modified Mercalli</th>
            <th>Rossi-Forel</th>
            <th>Japanese</th>
            <th>European</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>I</td>
            <td>I</td>
            <td>0</td>
            <td>I</td>
          </tr>
          <tr>
            <td>II</td>
            <td>I-II</td>
            <td>I</td>
            <td>II</td>
          </tr>
          <tr>
            <td>III</td>
            <td>III</td>
            <td>II</td>
            <td>III</td>
          </tr>
          <tr>
            <td>IV</td>
            <td>IV-V</td>
            <td>II-III</td>
            <td>IV</td>
          </tr>
          <tr>
            <td>V</td>
            <td>V-VI</td>
            <td>III</td>
            <td>V</td>
          </tr>
          <tr>
            <td>VI</td>
            <td>VI-VII</td>
            <td>IV</td>
            <td>VI</td>
          </tr>
          <tr>
            <td>VII</td>
            <td>VIII-</td>
            <td>IV-V</td>
            <td>VII</td>
          </tr>
          <tr>
            <td>VIII</td>
            <td>VIII+ to IX-</td>
            <td>V</td>
            <td>VIII</td>
          </tr>
          <tr>
            <td>IX</td>
            <td>IX+</td>
            <td>V-VI</td>
            <td>IX</td>
          </tr>
          <tr>
            <td>X</td>
            <td>X</td>
            <td>VI</td>
            <td>X</td>
          </tr>
          <tr>
            <td>XI</td>
            <td>-----</td>
            <td>VII</td>
            <td>XI</td>
          </tr>
          <tr>
            <td>XII</td>
            <td>-----</td>
            <td>-----</td>
            <td>XII</td>
          </tr>
        </tbody>
      </Table>
    </div>
  ),
};
